"use client";

import { CcResponsivePoster } from "./CcResponsivePoster";
import {
  ChartColumn,
  CircleCheck,
  FileText,
  Gauge,
  Globe,
  Inbox,
  LayoutTemplate,
  Link2,
  Mail,
  PenLine,
  Search,
  Smartphone,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

import type { ServiceVideoHeroScene as Scene } from "./CcServiceVideoHero";

const CHIP_ICONS: Record<string, LucideIcon> = {
  layout: LayoutTemplate,
  check: CircleCheck,
  phone: Smartphone,
  gauge: Gauge,
  pen: PenLine,
  inbox: Inbox,
  globe: Globe,
  search: Search,
  mail: Mail,
  chart: ChartColumn,
  file: FileText,
  link: Link2,
  trend: TrendingUp,
};

function HeroPoster({ src, first = false, thumbnail = false }: { src: string; first?: boolean; thumbnail?: boolean }) {
  const sizes = thumbnail ? "180px" : "100vw";
  return <CcResponsivePoster src={src} sizes={sizes} first={first} />;
}

type CcServiceVideoHeroMediaProps = {
  scenes: Scene[];
  tabsLabel: string;
};

/**
 * mollie.com's hero card with video: three scenes that cross-fade (with a
 * slow push-in), glass status chips that land one by one, and thumbnail tabs
 * with a progress line. Like the Fortnox hero, the card opens from the
 * content width to almost full width as it scrolls in. Clips are fetched only
 * when their scene is up next, and everything pauses off screen.
 */
export function CcServiceVideoHeroMedia({
  scenes,
  tabsLabel,
}: CcServiceVideoHeroMediaProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const reduced = useCcPrefersReducedMotion();
  const [active, setActive] = useState(0);
  const [inView, setInView] = useState(false);
  // Fetch clips only once the page has loaded and the browser is idle, so the
  // poster, fonts and scripts get the bandwidth first.
  const [loaded, setLoaded] = useState(false);
  // Only the active clip loads at first; the next one is warmed up halfway in.
  const [warmedFor, setWarmedFor] = useState(-1);
  const warmNext = warmedFor === active;

  useEffect(() => {
    let idle = 0;
    let timer = 0;
    const start = () => {
      if (typeof window.requestIdleCallback === "function") {
        idle = window.requestIdleCallback(() => setLoaded(true), {
          timeout: 2500,
        });
      } else {
        timer = window.setTimeout(() => setLoaded(true), 1200);
      }
    };
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
    return () => {
      window.removeEventListener("load", start);
      if (idle) window.cancelIdleCallback(idle);
      window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!loaded || !inView) return;
    const timer = window.setTimeout(
      () => setWarmedFor(active),
      (scenes[active].seconds * 1000) / 2,
    );
    return () => window.clearTimeout(timer);
  }, [active, inView, loaded, scenes]);

  // Open the card up as it scrolls in, and track whether it is on screen.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(([entry]) =>
      setInView(entry.isIntersecting),
    );
    observer.observe(root);
    if (reduced) {
      root.style.setProperty("--p", "1");
      return () => observer.disconnect();
    }
    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = root.getBoundingClientRect();
      const progress =
        (window.innerHeight - rect.top) / (window.innerHeight * 0.85);
      root.style.setProperty(
        "--p",
        Math.min(1, Math.max(0, progress)).toFixed(3),
      );
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [reduced]);

  // Play the active clip (and warm up the next); pause the rest.
  useEffect(() => {
    const shouldPlay = inView && loaded && !reduced;
    const small = window.matchMedia("(max-width: 810px)").matches;
    let current: HTMLVideoElement | null = null;
    videoRefs.current.forEach((video, index) => {
      if (!video) return;
      const isActive = index === active;
      const isNext = index === (active + 1) % scenes.length;
      if (
        loaded &&
        inView &&
        !reduced &&
        (isActive || (isNext && warmNext)) &&
        !video.getAttribute("src")
      ) {
        const clip = scenes[index];
        video.src = small && clip.videoSmall ? clip.videoSmall : clip.video;
      }
      if (isActive && shouldPlay) {
        current = video;
        // React doesn't set the muted attribute, and autoplay needs it.
        video.muted = true;
        video.currentTime = 0;
      } else {
        video.pause();
      }
    });
    const activeVideo = current as HTMLVideoElement | null;
    if (!activeVideo) return;
    // Retry once the clip can play or the tab comes back into view.
    const tryPlay = () => {
      if (document.visibilityState === "visible") {
        void activeVideo.play().catch(() => {});
      }
    };
    tryPlay();
    activeVideo.addEventListener("canplay", tryPlay);
    document.addEventListener("visibilitychange", tryPlay);
    return () => {
      activeVideo.removeEventListener("canplay", tryPlay);
      document.removeEventListener("visibilitychange", tryPlay);
    };
  }, [active, inView, loaded, reduced, scenes, warmNext]);

  const next = () => setActive((index) => (index + 1) % scenes.length);
  const scene = scenes[active];

  return (
    <div
      ref={rootRef}
      className="cc-wa-media"
      data-paused={!inView || reduced ? "true" : undefined}
    >
      <div className="cc-wa-media__frame">
        {scenes.map((item, index) => (
          <div
            key={item.id}
            className="cc-wa-media__scene"
            data-active={index === active ? "true" : undefined}
            style={{ ["--dur" as string]: `${item.seconds}s` }}
            aria-hidden={index === active ? undefined : "true"}
          >
            {index === 0 || index === active || (loaded && inView && warmNext && index === (active + 1) % scenes.length) ?
              <HeroPoster src={item.poster} first={index === 0} /> : null}
            <video
              ref={(node) => {
                videoRefs.current[index] = node;
              }}
              muted
              loop
              playsInline
              preload="none"
              aria-hidden="true"
              tabIndex={-1}
            />
          </div>
        ))}
        <span className="cc-wa-media__shade" aria-hidden="true" />

        {/* Status chips for the active scene, landing one by one. */}
        <ul key={scene.id} className="cc-wa-media__chips" aria-live="polite">
          {scene.chips.map((chip, index) => {
            const Icon = CHIP_ICONS[chip.icon] ?? CircleCheck;
            return (
              <li key={chip.text} style={{ ["--i" as string]: index }}>
                <Icon size={17} strokeWidth={1.8} aria-hidden="true" />
                {chip.text}
              </li>
            );
          })}
        </ul>

        {/* Thumbnail tabs with a progress line on the active one. */}
        <div
          className="cc-wa-media__tabs"
          role="tablist"
          aria-label={tabsLabel}
        >
          {scenes.map((item, index) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={index === active}
              className="cc-wa-media__tab"
              onClick={() => setActive(index)}
            >
              <span className="cc-wa-media__tab-label">{item.tab}</span>
              <span className="cc-wa-media__thumb">
                <HeroPoster src={item.poster} thumbnail />
              </span>
              <span className="cc-wa-media__progress">
                {index === active ? (
                  <i
                    key={`${item.id}-${active}`}
                    style={{ animationDuration: `${item.seconds}s` }}
                    onAnimationEnd={next}
                  />
                ) : null}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
